import { useState, lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { HardHat, KeyRound, Unlock } from "lucide-react";

const ConstructionScene = lazy(() => import("@/components/ConstructionScene"));

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("admin@legatum.com");
  const [password, setPassword] = useState("legatum2026");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(identifier, password);
      toast.success("Bienvenido a LEGATUM CAPITAL");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#090A0D]">
      {/* Left: 3D construction scene */}
      <div className="hidden lg:block flex-[1.35] relative overflow-hidden">
        <Suspense fallback={<div className="absolute inset-0 bg-[#090A0D]" />}>
          <ConstructionScene />
        </Suspense>
        {/* Gradient overlays */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[#090A0D]/60 via-transparent to-[#090A0D]" />
        <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none bg-gradient-to-t from-[#090A0D] to-transparent" />

        {/* Overlay content */}
        <div className="absolute inset-0 z-10 p-12 flex flex-col justify-between pointer-events-none">
          <motion.div {...fadeUp(0.1)} className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md flex items-center justify-center shadow-[0_0_24px_rgba(201,162,74,0.4)]"
                 style={{ background: "linear-gradient(135deg,#C9A24A,#8a6f2e)" }}>
              <span className="text-[#0b0b0b] font-bold tracking-wider">L</span>
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-[0.3em] text-white">LEGATUM</div>
              <div className="text-[10px] text-white/60 tracking-[0.32em]">CAPITAL · SAAS</div>
            </div>
          </motion.div>

          <div className="max-w-xl">
            <motion.div {...fadeUp(0.25)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A24A]/40 bg-[#C9A24A]/10 backdrop-blur-md mb-6">
              <HardHat className="h-3.5 w-3.5 text-[#C9A24A]" />
              <span className="text-[11px] tracking-[0.22em] uppercase text-[#C9A24A]">Plataforma en construcción activa</span>
            </motion.div>
            <motion.h1 {...fadeUp(0.35)}
              className="text-5xl xl:text-6xl font-bold leading-[1.03] tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">
              El sistema operativo de la{" "}
              <span className="font-extrabold" style={{ color: "#C9A24A" }}>construcción civil</span>.
            </motion.h1>
            <motion.p {...fadeUp(0.5)} className="mt-6 text-white/75 leading-relaxed max-w-md drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">
              Avales, pliegos, obras, garantías. Una sola plataforma para gobernar cada euro y cada documento.
            </motion.p>
            <motion.div {...fadeUp(0.65)} className="mt-8 flex gap-8">
              {[["40+", "Módulos operativos"], ["360°", "Control documental"], ["24/7", "Vigilancia PLACSP"]].map(([n, l]) => (
                <div key={l}>
                  <div className="text-2xl font-bold text-[#C9A24A]">{n}</div>
                  <div className="text-[11px] uppercase tracking-widest text-white/50 mt-1">{l}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative bg-[#0b0d11] text-white">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="h-9 w-9 rounded-md flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#C9A24A,#8a6f2e)" }}>
              <span className="text-[#0b0b0b] font-bold tracking-wider">L</span>
            </div>
            <div className="text-sm font-semibold tracking-[0.3em]">LEGATUM CAPITAL</div>
          </div>

          <h2 className="text-3xl font-semibold tracking-tight text-white">Iniciar sesión</h2>
          <p className="mt-2 text-sm text-white/55">
            Accede a tu panel de control empresarial.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="identifier" className="text-xs uppercase tracking-widest text-white/45">
                Email o nombre de usuario
              </Label>
              <Input
                id="identifier"
                data-testid="login-email-input"
                type="text"
                required
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="mt-2 bg-[#1C2028] border-white/10 text-white h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-white/45">
                Contraseña
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-[#1C2028] border-white/10 text-white h-11"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 mt-2 font-bold text-[#0b0b0b] border-0 shadow-lg hover:shadow-xl transition-shadow"
              style={{ background: "linear-gradient(135deg,#C9A24A,#8a6f2e)" }}
            >
              {loading ? "Accediendo…" : "Acceder"}
            </Button>
          </div>

          {/* Credenciales visibles durante la fase de construcción */}
          <motion.div
            {...fadeUp(0.4)}
            data-testid="admin-credentials-card"
            className="mt-6 rounded-lg border border-dashed border-[#C9A24A]/50 bg-[#C9A24A]/[0.06] p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <Unlock className="h-3.5 w-3.5 text-[#C9A24A]" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#C9A24A]">
                Acceso abierto · fase de construcción
              </span>
            </div>
            <div className="space-y-1.5 text-sm font-mono">
              <div className="flex items-center gap-2">
                <span className="text-white/40 w-24 text-xs uppercase tracking-wider font-sans">Usuario</span>
                <span data-testid="admin-credentials-email" className="text-white/90">admin@legatum.com</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/40 w-24 text-xs uppercase tracking-wider font-sans">Contraseña</span>
                <span data-testid="admin-credentials-password" className="text-white/90">legatum2026</span>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-[var(--legatum-text-muted)]">
              <KeyRound className="h-3 w-3" />
              Claves temporales visibles mientras la plataforma está en obras.
            </div>
          </motion.div>

          <div className="mt-6 text-center text-xs text-white/40">
            ¿No tienes cuenta?{" "}
            <Link to="/register" data-testid="register-link" className="text-[#C9A24A] hover:underline">
              Solicitar acceso
            </Link>
          </div>
        </motion.form>
        <Toaster theme="dark" position="bottom-right" />
      </div>
    </div>
  );
}
